/**
 * src/types/charts/bar.chart.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * Bar chart — all types, spacing, animation styles, and defaults.
 *
 * ADDING TO THIS FILE: only change this file and chart.registry.ts.
 */

import type { ChartDataPoint } from '../core/interfaces.shared';
import {
  makeTitle, makeSubtitle,
  DEFAULT_BACKGROUND, DEFAULT_LAYOUT, DEFAULT_AXES, DEFAULT_LEGEND, DEFAULT_DATA_LABELS,
} from '../core/defaults.shared';
import type { ChartConfigBase } from '../core/chart-config.base';
import type { SchemaNote } from '../chart.schema';

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION STYLES
// ─────────────────────────────────────────────────────────────────────────────

export type BarAnimationStyle =
  | 'classic' | 'sequential' | 'staggered' | 'elastic' | 'wave'
  | 'reveal' | 'fadeScale' | 'popIn' | 'magnitudeStagger';

export const BAR_ANIMATION_STYLES: BarAnimationStyle[] = [
  'classic', 'sequential', 'staggered', 'elastic', 'wave',
  'reveal', 'fadeScale', 'popIn', 'magnitudeStagger',
];

/** Per-point animation override options (excludes whole-chart-only styles). */
export const BAR_ELEMENT_PANEL_STYLES: BarAnimationStyle[] = BAR_ANIMATION_STYLES;


// ─────────────────────────────────────────────────────────────────────────────
// DATA POINT
// ─────────────────────────────────────────────────────────────────────────────

export interface BarDataPoint extends ChartDataPoint {
  chartType:    'bar';
  value:        number;    // bar height. Negative values render below the zero baseline.
  group?:       string;    // group key for grouped bar charts.
  showValue?:   boolean;   // show value label above bar. Default: true.
  showLegend?:  boolean;
  labelOffsetX?: number;
  labelOffsetY?: number;
}


// ─────────────────────────────────────────────────────────────────────────────
// SPACING CONFIG
// ─────────────────────────────────────────────────────────────────────────────

export interface BarSpacingConfig {
  chartType:    'bar';
  barWidth:     number;    // px per bar. Range: 10–400. Default: 200.
  barSpacing:   number;    // gap between bars within a group. Range: 0–300. Default: 75.
}


// ─────────────────────────────────────────────────────────────────────────────
// DEFAULTS
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_DEFAULTS: ChartConfigBase<BarSpacingConfig> = {
  chartType: 'bar',
  content: {
    titles:    [makeTitle('Monthly Performance')],
    subtitles: [makeSubtitle('Jan – Jun 2024')],
    infoBoxes: [],
  },
  animation:  { duration: 5, style: 'fadeScale', textSpeed: 1 },
  background: { ...DEFAULT_BACKGROUND },
  layout:     { ...DEFAULT_LAYOUT },
  axes:       { ...DEFAULT_AXES },
  legend:     { ...DEFAULT_LEGEND },
  dataLabels: { ...DEFAULT_DATA_LABELS },
  spacing: {
    chartType:    'bar',
    barWidth:     200,
    barSpacing:   75,
  },
  export: { quality: '1080p', isLooping: true, canvasFormat: '16:9' },
};

// ─────────────────────────────────────────────────────────────────────────────
// SCHEMA NOTES — consumed by chart.schema.ts merger and getChartSchemaSlice()
// ─────────────────────────────────────────────────────────────────────────────

export const BAR_SCHEMA_NOTES: Record<string, SchemaNote> = {

  'bar.barWidth': {
    description: 'Width of each individual bar in canvas pixels.',
    type:    'number',
    range:   '10–400',
    default: 200,
    unit:    'canvas pixels',
    affects: ['bar visual width', 'total chart width'],
    bounds:  ['Min 10. Max 400.'],
    synergizes: ['bar.barSpacing — the two together control how "chunky" the chart feels'],
    tensions:   ['data count — wide bars with many data points will cause overflow past chart margins'],
    agentNote:  "At 1920px canvas width with default margins (~1710px chart width), 200px × N bars + barSpacing fits comfortably for N ≤ 6. For N > 8, reduce barWidth to 100–150. For N > 12, reduce to 60–80.",
  },

  'bar.barSpacing': {
    description: 'Gap between individual bars (or bar groups) in canvas pixels.',
    type:    'number',
    range:   '0–300',
    default: 75,
    unit:    'canvas pixels',
    affects: ['inter-bar gap'],
    bounds:  ['Min 0 (bars touch). Max 300.'],
    synergizes: ['bar.barWidth'],
    tensions:   ['data count — high barSpacing with many bars will push bars past chart margins'],
    agentNote:  "For single-series bar charts, barSpacing controls the gap between bars. For grouped charts, barSpacing controls the gap between bar groups.",
  },

  'dataPoint.bar.value': {
    description: 'Numeric height value for a single bar. Bar height is proportional to (value / maxValue) × chart plot height.',
    type:    'number',
    range:   'any positive number; zero is valid (renders as empty/stub bar)',
    default: null,
    unit:    'arbitrary numeric unit (user-defined)',
    affects: ['bar height', 'y-axis tick range', 'value label position above bar'],
    bounds:  ['Negative values supported — bars render below the zero baseline.'],
    synergizes: ['dataLabels.style.fontSize — value labels are drawn above bars; scale down if bars are short'],
    tensions:   ['max/min ratio — if one bar is 10× the others, all other bars appear as thin slivers'],
    agentNote:  "Negative values are supported. Bars extend below the zero baseline. The Y axis auto-adjusts (switches from 'zero' to 'auto' minValue) when negatives are present. A dashed zero baseline line is drawn automatically.",
  },

};
